// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import type { Empty } from "@bufbuild/protobuf/wkt"
import { showSnackBar } from "../../snackbar/actions"
import * as Actions from "../actions"
import * as Slice from "../slice"
import * as TimelineActions from "../../timeline/actions"
import { Code, executorMutation, isConnectError, timesheetFieldService } from "../../../app/rpc"
import {
  EnableTimesheetFieldOptionsRequest,
  EnableTimesheetFieldOptionsRequestSchema,
  ListTimesheetFieldsRequestSchema,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type EnableTimesheetFieldRpc = (request: EnableTimesheetFieldOptionsRequest) => Observable<Empty>

export const enableTimesheetFieldOptions = (enableTimesheetFieldOptionsRpc: EnableTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.enableTimesheetFieldOptions.match),
      map(action => action.payload),
      mergeMap(payload => {
        const request = create(EnableTimesheetFieldOptionsRequestSchema, payload)
        return enableTimesheetFieldOptionsRpc(request).pipe(
          mergeMap(() => of(Slice.enableTimesheetFieldOptionsFulfilled(request))),
          catchError(e => handleError(e, payload.siteId ?? "")),
        )
      }),
    )

const handleError = (e: unknown, siteId: string): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return concat([
        TimelineActions.listTimesheetFields({
          request: create(ListTimesheetFieldsRequestSchema, { siteId }),
          includeOptions: true,
        }),
        showSnackBar({
          message: t({
            id: "msg_timesheet_field_not_found",
            message: "Timesheet field not found.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({
          id: "msg_enable_timesheet_field_option_permission_denied",
          message: "Failed to enable timesheet field option. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_enable_timesheet_field_option_failed",
      message: "Failed to enable timesheet field option. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const enableTimesheetFieldOptionsRpc = (request: EnableTimesheetFieldOptionsRequest): Observable<Empty> =>
  executorMutation(() => timesheetFieldService.enableTimesheetFieldOptions(request))

export default enableTimesheetFieldOptions(enableTimesheetFieldOptionsRpc)
